import React, { useState, useEffect } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getFaqQuestion, getFaqAnswer } from '../i18n/localizeContent';

interface FaqItem {
  _id: string;
  question: string;
  answer: string;
  i18n?: {
    pt?: { question?: string; answer?: string };
    en?: { question?: string; answer?: string };
    es?: { question?: string; answer?: string };
  };
  order: number;
}

const FAQ: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const lang = i18n.language?.split('-')[0] || 'pt';

  useEffect(() => {
    axios
      .get('/faq')
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 text-primary-600 mb-4">
            <HelpCircle className="w-8 h-8" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">{t('faq.title')}</h1>
          <p className="mt-2 text-lg text-gray-600">{t('faq.subtitle')}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">{t('faq.empty')}</div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => {
              const isOpen = openId === item._id;
              const question = getFaqQuestion(item, lang);
              const answer = getFaqAnswer(item, lang);
              return (
                <div
                  key={item._id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggle(item._id)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{question}</span>
                    {isOpen ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-4 pt-0">
                      <div className="text-gray-600 leading-relaxed whitespace-pre-wrap border-t border-gray-100 pt-4">
                        {answer}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQ;
